import { DateValue } from "@cmp-src/calendar/types";
import { differenceInDays, format, isToday, startOfDay } from "date-fns";

type OutFmt = "ymd" | "dmy" | "mdy";

export function getFormats(outputFormat: OutFmt, showDayName = false) {
  switch (outputFormat) {
    case "ymd":
      return {
        range: "yyyy-MM-dd",
        single: `${showDayName ? "EE " : ""}yyyy-MM-dd`
      };
    case "dmy":
      return {
        range: "dd/MM",
        single: `${showDayName ? "EE " : ""}dd/MM/yyyy`
      };
    default:
      return {
        range: "MM/dd",
        single: `${showDayName ? "EE " : ""}MM/dd/yyyy`
      };
  }
}

export function splitDatesIntoIntervals(
  dates: DateValue | undefined,
  outputFormat: OutFmt = "mdy",
  showDayName?: boolean
): ([string, string] | [string])[] {
  const intervals: ([string, string] | [string])[] = [];
  if (!dates) return [];

  const { range: rangeFmt, single: singleFmt } = getFormats(
    outputFormat,
    showDayName
  );

  if (dates instanceof Date) {
    return [[isToday(dates) ? "Today" : format(dates, singleFmt)]];
  }

  // Handle DateRange object (has 'from' and 'to' properties)
  if (!Array.isArray(dates) && typeof dates === "object" && "from" in dates) {
    if (!dates.from || !dates.to) return [];
    // check if both from and to are today and equal
    if (
      isToday(dates.from) &&
      isToday(dates.to) &&
      startOfDay(dates.from).getTime() === startOfDay(dates.to).getTime()
    ) {
      return [["Today"]];
    }
    return [[format(dates.from, rangeFmt), format(dates.to, rangeFmt)]];
  }

  if (dates.length === 0) return [];
  if (dates.length === 1) {
    return [[isToday(dates[0]) ? "Today" : format(dates[0], singleFmt)]];
  }

  let start = format(dates[0], rangeFmt);
  let previous = startOfDay(dates[0]);

  for (let i = 1; i < dates.length; i++) {
    const current = startOfDay(dates[i]);

    if (differenceInDays(current, previous) > 1) {
      if (start !== format(previous, rangeFmt)) {
        intervals.push([start, format(previous, rangeFmt)]);
      } else {
        intervals.push([start]);
      }
      start = format(current, rangeFmt);
    }
    previous = current;
  }

  if (start !== format(previous, rangeFmt)) {
    intervals.push([start, format(previous, rangeFmt)]);
  } else {
    intervals.push([start]);
  }

  return intervals;
}
